import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import { useSelector } from "react-redux";
import { Navigate, useParams } from "react-router-dom";
import MapComponent from "../../components/MAPA-COMP/mapa-comp";
import firebase from "../../config/firebase";
import freightService from "../../service/freight.service";
import StepPoints from "../../components/step-folow-driver/comp-step-driver";
import CardFolowFreigth from "../../components/card-info-folow-freight/card-freigth-folow";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import paymentService from "../../service/payment.service";
import driverService from "../../service/driver.service";
import { QRCodeCanvas } from "qrcode.react";

require("firebase/auth");

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function toDateSafe(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(value) {
  const d = toDateSafe(value);
  return d ? d.toLocaleString("pt-BR") : "N/A";
}

function getFreightStatusLabel(freightData) {
  return (
    freightData?.status?.describe ||
    freightData?.status?.code ||
    "Sem status"
  );
}

function isFreightFinalizedStatus(freightData) {
  const statusLabel = normalizeText(
    freightData?.status?.describe || freightData?.status?.code || ""
  );
  return statusLabel.includes("finalizado");
}

function getProgressSummary(stops = []) {
  const total = stops.length;
  const completed = stops.filter((s) => s?.concluded).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { total, completed, percent };
}

function getGpsHealth(positions = []) {
  if (!positions.length) {
    return { label: "Sem sinal", severity: "high", minutesWithoutUpdate: null };
  }
  const latestTs = Math.max(
    ...positions
      .map((p) => Number(p?.timestamp || 0))
      .filter((n) => Number.isFinite(n) && n > 0)
  );
  if (!latestTs) {
    return { label: "Sem sinal", severity: "high", minutesWithoutUpdate: null };
  }
  const minutes = Math.max(0, Math.floor((Date.now() - latestTs) / 60000));
  if (minutes <= 15) {
    return { label: "Bom", severity: "low", minutesWithoutUpdate: minutes };
  }
  if (minutes <= 45) {
    return { label: "Atenção", severity: "medium", minutesWithoutUpdate: minutes };
  }
  return { label: "Crítico", severity: "high", minutesWithoutUpdate: minutes };
}

function estimateDynamicEta(positions = [], stops = []) {
  const nextStop = stops.find((s) => !s?.concluded);
  if (!nextStop || positions.length < 2) {
    return null;
  }
  const ordered = [...positions]
    .filter((p) => Array.isArray(p?.coords) && p.coords.length === 2)
    .sort((a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0));
  const prev = ordered[ordered.length - 2];
  const curr = ordered[ordered.length - 1];
  if (!prev || !curr) return null;
  const dtHours = Math.max(
    0.0001,
    (Number(curr.timestamp || 0) - Number(prev.timestamp || 0)) / 3600000
  );
  const dLat = curr.coords[0] - prev.coords[0];
  const dLng = curr.coords[1] - prev.coords[1];
  const speedUnits = Math.sqrt(dLat * dLat + dLng * dLng) / dtHours;
  if (!Number.isFinite(speedUnits) || speedUnits <= 0) return null;
  const remLat = Number(nextStop.latitude || 0) - curr.coords[0];
  const remLng = Number(nextStop.longitude || 0) - curr.coords[1];
  const remainingUnits = Math.sqrt(remLat * remLat + remLng * remLng);
  const etaHours = remainingUnits / speedUnits;
  if (!Number.isFinite(etaHours) || etaHours <= 0) return null;
  return new Date(Date.now() + etaHours * 3600000);
}

function buildOperationalAlerts({ freightData, gpsHealth, progress, etaDate }) {
  const alerts = [];
  const statusLabel = getFreightStatusLabel(freightData);
  const normalizedStatus = normalizeText(statusLabel);
  if (normalizedStatus.includes("finalizado")) {
    return alerts;
  }

  if (gpsHealth?.severity === "high") {
    alerts.push({
      key: "gps-sem-atualizacao",
      level: "alto",
      title: "GPS sem atualização",
      source: "GPS",
      description: "Última posição está desatualizada e pode comprometer o monitoramento.",
    });
  }

  if (etaDate) {
    const deadline = toDateSafe(
      freightData?.freight?.firstDelivery?.dateDelivery ||
        freightData?.firstDelivery?.dateDelivery
    );
    if (deadline && etaDate > deadline) {
      alerts.push({
        key: "atraso-projetado-eta",
        level: "medio",
        title: "Atraso projetado por ETA",
        source: "ETA",
        description: "A previsão dinâmica indica entrega após o prazo planejado.",
      });
    }
  }

  if (
    normalizedStatus.includes("em transito") &&
    progress.total > 0 &&
    progress.percent < 25
  ) {
    alerts.push({
      key: "progresso-abaixo-esperado",
      level: "medio",
      title: "Progresso abaixo do esperado",
      source: "Pontos de parada",
      description: "Poucos pontos concluídos para o estágio atual da viagem.",
    });
  }

  const hasUnplannedStop = (freightData?.subcollectionData || []).some((stop) =>
    String(stop?.name || "").toLowerCase().includes("nao planej")
  );
  if (hasUnplannedStop) {
    alerts.push({
      key: "parada-nao-planejada",
      level: "alto",
      title: "Parada não planejada confirmada",
      source: "Pontos de parada",
      description: "Foi detectada parada fora da rota planejada.",
    });
  }

  return alerts;
}

export default function ViewerFreights() {
  const db = firebase.firestore();
  const { id } = useParams();
  const user = useSelector(state=> state.user);

  const [points, setPoints] = useState([]);
  const [positions, setPositions] = useState([]);
  const [freightData, setFreightData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stoppingPointsUrl, setStoppingPointsUrl] = useState("");
  const [positionsUrl, setPositionsUrl] = useState("");
  const [nameDriver, setNameDriver] = useState("");
  const [publicTrackUrl, setPublicTrackUrl] = useState("");
  const [isGeneratingTrackLink, setIsGeneratingTrackLink] = useState(false);
  const [trackLinkValidityHours, setTrackLinkValidityHours] = useState(72);
  const [alertsHistory, setAlertsHistory] = useState([]);
  const [alertFilter, setAlertFilter] = useState("todos");
  const [timelineFilter, setTimelineFilter] = useState("todos");
  const isFreightFinished = useMemo(() => {
    const stops = freightData?.subcollectionData || [];
    if (stops.length === 0) return false;
    const lastStop = stops[stops.length - 1];
    return lastStop?.concluded === true;
  }, [freightData]);
  const isFreightFinalized = useMemo(
    () => isFreightFinished || isFreightFinalizedStatus(freightData),
    [isFreightFinished, freightData]
  );
  const journeyWindow = useMemo(() => {
    const stops = freightData?.subcollectionData || [];
    const stopDates = stops
      .map((stop) => toDateSafe(stop?.timestamp || stop?.updatedAt || stop?.createdAt))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());
    const startDate = stopDates[0] || null;
    const endByStops = stopDates.length ? stopDates[stopDates.length - 1] : null;
    const endByHistory = toDateSafe(freightData?.history?.dateFinished);
    const endDate = endByHistory || endByStops || null;
    return { startDate, endDate };
  }, [freightData]);
  const progress = useMemo(
    () => getProgressSummary(freightData?.subcollectionData || []),
    [freightData]
  );
  const gpsHealth = useMemo(() => getGpsHealth(positions), [positions]);
  const dynamicEta = useMemo(
    () => estimateDynamicEta(positions, freightData?.subcollectionData || []),
    [positions, freightData]
  );
  const operationalAlerts = useMemo(
    () =>
      buildOperationalAlerts({
        freightData,
        gpsHealth,
        progress,
        etaDate: dynamicEta,
      }),
    [freightData, gpsHealth, progress, dynamicEta]
  );
  const displayedAlerts = useMemo(() => {
    if (alertFilter === "todos") return alertsHistory;
    return alertsHistory.filter((a) => normalizeText(a?.level) === alertFilter);
  }, [alertsHistory, alertFilter]);
  const timelineItems = useMemo(() => {
    const stopEvents = (freightData?.subcollectionData || []).map((stop) => ({
      type: "parada",
      title: `${stop?.name || "Parada"} ${stop?.concluded ? "(concluída)" : "(pendente)"}`,
      date: toDateSafe(stop?.timestamp || stop?.updatedAt || stop?.createdAt),
      source: "Ponto de parada",
    }));
    const alertEvents = alertsHistory.map((alert) => ({
      type: "alerta",
      title: alert?.title || "Alerta",
      date: toDateSafe(alert?.createdAt),
      source: alert?.source || "Sistema",
    }));
    let all = [...stopEvents, ...alertEvents]
      .filter((item) => item.date)
      .sort((a, b) => b.date.getTime() - a.date.getTime());
    if (isFreightFinalized && journeyWindow.endDate) {
      const startMs = journeyWindow.startDate
        ? journeyWindow.startDate.getTime()
        : null;
      const endMs = journeyWindow.endDate.getTime();
      all = all.filter((item) => {
        const itemMs = item.date.getTime();
        if (startMs && itemMs < startMs) return false;
        return itemMs <= endMs;
      });
    }
    if (timelineFilter === "todos") return all;
    return all.filter((item) => item.type === timelineFilter);
  }, [freightData, alertsHistory, timelineFilter, isFreightFinalized, journeyWindow]);

  // Função para calcular a distância entre duas coordenadas (Haversine Formula)
  const calculateDistance = (coord1, coord2) => {
    const [lat1, lon1] = coord1;
    const [lat2, lon2] = coord2;
    const R = 6371; // Raio da Terra em km

    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distância em km

    return distance;
  };

  // Função para gerar o link de coordenadas filtradas e unificadas
  const generateGoogleMapsLink_ = (positions) => {
    // 23 a 25
    const baseUrl = "https://www.google.com/maps/dir/";

    // Função para agrupar coordenadas muito próximas e com timestamps próximos
    const filteredPositions = positions.reduce((acc, current, index, arr) => {
      if (index === 0) {
        acc.push(current); // Adiciona o primeiro ponto
        return acc;
      }

      const prevPosition = acc[acc.length - 1]; // Último ponto no array de acumulados
      const distance = calculateDistance(current.coords, prevPosition.coords);
      const timeDifference = Math.abs(
        current.timestamp - prevPosition.timestamp
      );

      // Se a distância for maior que 0.1 km ou a diferença de tempo for maior que 5 minutos, adiciona como um novo ponto
      if (distance > 0.1 || timeDifference > 5 * 60 * 1000) {
        acc.push(current);
      } else {
        // Se for muito próximo, substitui o ponto anterior com a média dos pontos agrupados
        const avgLat = (prevPosition.coords[0] + current.coords[0]) / 2;
        const avgLon = (prevPosition.coords[1] + current.coords[1]) / 2;
        prevPosition.coords = [avgLat, avgLon];
        prevPosition.timestamp = Math.max(
          prevPosition.timestamp,
          current.timestamp
        ); // Mantenha o timestamp mais recente
      }

      return acc;
    }, []);

    // Transformar as coordenadas filtradas em string para o Google Maps
    const coordsString = filteredPositions
      .map((position) => position.coords.join(","))
      .join("/");

    return `${baseUrl}${coordsString}`;
  };


  const generateGoogleMapsLink = (positions) => {
    const baseUrl = "https://www.google.com/maps/dir/";
    const maxPoints = 25;
  
    if (!Array.isArray(positions) || positions.length === 0) {
      return baseUrl;
    }
  
    const limit = Math.min(positions.length, maxPoints);
  
    let selectedPositions;
  
    if (positions.length <= maxPoints) {
      selectedPositions = positions;
    } else {
      const step = Math.floor(positions.length / (limit - 1));
      selectedPositions = [positions[0]];
  
      for (let i = step; i < positions.length - 1; i += step) {
        selectedPositions.push(positions[i]);
      }
  
      selectedPositions.push(positions[positions.length - 1]);
    }
  
    const coordsString = selectedPositions
      .map((position) => {
        if (
          Array.isArray(position.coords) &&
          position.coords.length === 2 &&
          typeof position.coords[0] === "number" &&
          typeof position.coords[1] === "number"
        ) {
          return position.coords.join(",");
        }
        return null;
      })
      .filter(Boolean)
      .join("/");
  
    return `${baseUrl}${coordsString}`;
  };
  


  // const generateGoogleMapsLink = (positions) => {
  //   const baseUrl = "https://www.google.com/maps/dir/";

  //   // Limite de pontos para o Google Maps (25 é o máximo permitido)
  //   const maxPoints = 25;

  //   if (positions.length <= maxPoints) {
  //     // Se o número de posições for menor ou igual ao limite, basta usar todas as posições
  //     const coordsString = positions
  //       .map((position) => position.coords.join(","))
  //       .join("/");
  //     return `${baseUrl}${coordsString}`;
  //   }

  //   // Selecionar os primeiros, últimos e pontos intermediários proporcionalmente
  //   const step = Math.floor(positions.length / (maxPoints - 1)); // -1 para incluir o último ponto
  //   const selectedPositions = [];

  //   // Adiciona o primeiro ponto
  //   selectedPositions.push(positions[0]);

  //   // Adiciona pontos intermediários proporcionalmente
  //   for (let i = step; i < positions.length - 1; i += step) {
  //     selectedPositions.push(positions[i]);
  //   }

  //   // Adiciona o último ponto
  //   selectedPositions.push(positions[positions.length - 1]);

  //   // Gerar a string para o Google Maps com os pontos selecionados
  //   const coordsString = selectedPositions
  //     .map((position) => position.coords.join(","))
  //     .join("/");

  //   return `${baseUrl}${coordsString}`;
  // };

  const fetchFreightData = async () => {
    try {
      // const freightDocRef = db.collection("freight").doc(id);
      // const freightDoc = await freightDocRef.get();
      const freightDoc = await freightService?.getFreightById(id);

      if (!freightDoc.exists) {
        throw new Error("Documento não encontrado");
      }

      const freightData = {
        id: freightDoc.id,
        ...freightDoc.data(),
      };

      // const subcollectionSnapshot = await freightDocRef
      //   .collection("stopping_points")
      //   .get();

      const subcollectionSnapshot = await freightService?.getStoppingPointsByFreight(
        id
      );

      const subcollectionData = subcollectionSnapshot.docs.map((subDoc) => ({
        id: subDoc.id,
        ...subDoc.data(),
      }));

      setFreightData({
        ...freightData,
        subcollectionData,
      });

      const pontos = subcollectionData.map((item) => ({
        coords: [item.latitude, item.longitude],
        popupText: `
          <strong>${item.name}</strong><br/>
          ${item.logradouro}, ${item.number}, ${item.city} - ${item.uf}, ${item.cep}
        `,
        events: item.events || [], // Inclui os eventos associados ao ponto de parada
        operation: item.operation || [],
        concluded: item.concluded || false,
        locationTime:
          item.timestamp ?? item.createdAt ?? item.updatedAt ?? null,
      }));

      // Adiciona os pontos de parada ao estado
      setPoints(pontos);
      // Gerar a URL de pontos de parada
      setStoppingPointsUrl(generateGoogleMapsLink(pontos));
    } catch (err) {
      setError(err);
      console.error("Erro ao buscar dados do frete:", err);
    } finally {
      setLoading(false);
    }
  };

  const searchLatestPositions = async (lastPositionReceived) => {
    try {
      console.log(lastPositionReceived);
  
      const latestPositionsSnapshot = await freightService?.searchLatestPositions(
        id,
        lastPositionReceived
      );
  
      const latestPositions = latestPositionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      // Criar novas coordenadas
      const coordenadas = latestPositions.map((position) => ({
        coords: [position.latitude, position.longitude],
        timestamp: position.timestamp,
      }));
  
      setPositions((_positions) => {
        const novasPosicoes = coordenadas.filter(
          (novaPosicao) =>
            !_positions.some((pos) => pos.timestamp === novaPosicao.timestamp)
        );
  
        const todasAsPosicoes = [..._positions, ...novasPosicoes];
        // Atualizar a URL com todas as posições
        setPositionsUrl(generateGoogleMapsLink(todasAsPosicoes));
  
        return todasAsPosicoes;
      });
  
      return latestPositions;
    } catch (error) {
      console.error("Erro ao buscar últimas posições:", error);
      return null;
    }
  };

  async function nameDriverFreight(freightData) {
    if (freightData) {
      let data = await driverService.getDriverAvailable(freightData.freight.getDriverFreight.uidDriver);

      const docSnap = await driverService.getDriverAvailable( freightData.freight.getDriverFreight.uidDriver );

      if (docSnap.exists) {
        const driverData = docSnap.data();
        console.log(driverData);
        console.log(driverData.name);

        setNameDriver(driverData.name);
      } else {
        console.log("Motorista não encontrado");
      }
    }
  }

  const generatePublicTrackUrlFromToken = (token) => {
    if (!token) return "";
    return `${window.location.origin}/track/${token}`;
  };

  const generateSecureToken = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  };

  const handleGeneratePublicTrackLink = async () => {
    try {
      setIsGeneratingTrackLink(true);
      const token = generateSecureToken();
      const expiresAt = new Date(
        Date.now() + trackLinkValidityHours * 60 * 60 * 1000
      );
      await freightService.setPublicTrackingData(id, {
        token,
        active: true,
        createdAt: new Date(),
        expiresAt,
      });
      const url = generatePublicTrackUrlFromToken(token);
      setPublicTrackUrl(url);
      await navigator.clipboard.writeText(url);
      alert("Link de rastreio gerado e copiado.");
    } catch (e) {
      console.error("Erro ao gerar link público:", e);
      alert("Não foi possível gerar o link de rastreio.");
    } finally {
      setIsGeneratingTrackLink(false);
    }
  };

  const handleDisablePublicTrackLink = async () => {
    try {
      await freightService.disablePublicTracking(id);
      setPublicTrackUrl("");
      alert("Link de rastreio desativado.");
    } catch (e) {
      console.error("Erro ao desativar link público:", e);
      alert("Não foi possível desativar o link.");
    }
  };

  const handleShareViaWhatsApp = () => {
    if (!publicTrackUrl) {
      alert("Gere um link público antes de compartilhar.");
      return;
    }
    const msg = `Acompanhe a rota em tempo real: ${publicTrackUrl}`;
    const waUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  // const searchLatestPositions = async (lastPositionReceived) => {
  //   try {
  //     console.log(lastPositionReceived);

  //     const latestPositionsSnapshot = await freightService?.searchLatestPositions(
  //       id,
  //       lastPositionReceived
  //     );

  //     const latestPositions = latestPositionsSnapshot.docs.map((doc) => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     }));

  //     // Atualizando o estado com as últimas coordenadas recebidas
  //     const coordenadas = latestPositions.map((position) => ({
  //       coords: [position.latitude, position.longitude],
  //       timestamp: position.timestamp,
  //     }));

  //     // setPositions((_positions) => [..._positions, ...coordenadas]);
  //     setPositions((_positions) => {
  //       const novasPosicoes = coordenadas.filter(novaPosicao =>
  //         !_positions.some(pos => pos.timestamp === novaPosicao.timestamp)
  //       );
  //       return [..._positions, ...novasPosicoes];
  //     });
      

  //     // Gerar a URL de posições

      

  //     const positionCoords = coordenadas.map((pos) => pos.coords);
  //     setPositionsUrl(generateGoogleMapsLink([...positions, ...coordenadas]));

  //     return latestPositions;
  //   } catch (error) {
  //     console.error("Erro ao buscar últimas posições:", error);
  //     return null;
  //   }
  // };

  useEffect(() => {
    fetchFreightData();
    searchLatestPositions(null); // Chamando a função para buscar as últimas posições
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    const unsubscribe = db
      .collection("freight")
      .doc(id)
      .collection("alerts_history")
      .orderBy("createdAt", "desc")
      .limit(100)
      .onSnapshot(
        (snapshot) => {
          const docs = (snapshot.docs || []).map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setAlertsHistory(docs);
        },
        (err) => {
          console.error("Erro ao carregar histórico de alertas:", err);
        }
      );
    return () => unsubscribe && unsubscribe();
  }, [db, id]);

  // A gravacao de alerts_history saiu daqui: o painel nao escreve no banco, toda escrita passa
  // pelas validacoes do backend. E o createdAt era o relogio do navegador, nao o do servidor.
  // A leitura acima fica — ler nao e o que a regra proibe.

  useEffect(() => {
    if (freightData) {
      nameDriverFreight(freightData);
      const existingToken = freightData?.publicTracking?.token;
      const isActive = freightData?.publicTracking?.active !== false;
      const expRaw = freightData?.publicTracking?.expiresAt;
      const createdRaw = freightData?.publicTracking?.createdAt;
      const expMs = expRaw?.toDate
        ? expRaw.toDate().getTime()
        : expRaw
        ? new Date(expRaw).getTime()
        : null;
      const createdMs = createdRaw?.toDate
        ? createdRaw.toDate().getTime()
        : createdRaw
        ? new Date(createdRaw).getTime()
        : null;
      const notExpired = !expMs || expMs > Date.now();
      if (expMs && createdMs && expMs > createdMs) {
        const diffHours = Math.round((expMs - createdMs) / (60 * 60 * 1000));
        if ([24, 72, 168].includes(diffHours)) {
          setTrackLinkValidityHours(diffHours);
        }
      }
      setPublicTrackUrl(
        existingToken && isActive && notExpired
          ? generatePublicTrackUrlFromToken(existingToken)
          : ""
      );
    }
  }, [freightData]);

  useEffect(() => {
    if (points.length > 0) {
      console.log("Pontos no estado:", points);
    }
  }, [points]); // Verificar sempre que `points` for atualizado

  if (useSelector((state) => state.usuarioLogado) <= 0) {
    return <Navigate to="/login" />;
  }

  return (
    <NewMiniDrawer
      divOpen={
        <div className="freight-content-details">
          <div className="container">
            {loading ? (<p>Carregando...</p>) : error ? (<p>Erro ao carregar dados: {error.message}</p>) : points.length > 0 ? (
              <>
                {/* <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                  }}
                > */}
                  {/* <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 1,
                        color: "#fff",
                        background: "#012443",
                        borderRadius: 999,
                        padding: "5px 10px",
                        lineHeight: 1,
                      }}
                    >
                      FORTIO
                    </div>
                    <span style={{ color: "#334155", fontSize: 14 }}>
                      {nameDriver
                        ? `O motorista ${nameDriver} está na estrada realizando as entregas.`
                        : "Acompanhamento em tempo real da rota de entregas."}
                    </span>
                  </div> */}
                {/* </div> */}
                <section className="freight-executive-grid">
                  <article className="freight-kpi-card">
                    <h4>Status da viagem</h4>
                    <strong>{getFreightStatusLabel(freightData)}</strong>
                  </article>
                  <article className="freight-kpi-card">
                    <h4>Progresso das paradas</h4>
                    <strong>
                      {progress.completed}/{progress.total} ({progress.percent}%)
                    </strong>
                  </article>
                  <article className="freight-kpi-card">
                    <h4>Saúde do rastreio GPS</h4>
                    <strong>{isFreightFinalized ? "Status finalizado" : gpsHealth.label}</strong>
                    {!isFreightFinalized && gpsHealth.minutesWithoutUpdate !== null && (
                      <small>{gpsHealth.minutesWithoutUpdate} min sem atualização</small>
                    )}
                  </article>
                  <article className="freight-kpi-card">
                    <h4>ETA dinâmico</h4>
                    <strong>{dynamicEta ? formatDateTime(dynamicEta) : "Indisponível"}</strong>
                  </article>
                </section>

                
                <MapComponent
                  points={points}
                  idFreight={id}
                  searchLatestPositions={searchLatestPositions}
                  mapHeight={640}
                  freightStatusDescribe={freightData?.status?.describe}
                />
                {isFreightFinished && (
                  <div
                    style={{
                      marginTop: 12,
                      marginBottom: 4,
                      background: "#1f9d55",
                      color: "#fff",
                      borderRadius: 10,
                      padding: "10px 14px",
                      fontWeight: 600,
                    }}
                  >
                    Este frete já teve as entregas realizadas e foi finalizado.
                  </div>
                )}
                <section
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-around",
                    width: "100%",
                    gap: 10,
                    paddingTop: '20px'
                  }}
                >
                  <div style={{ width: "50%"}}>
                    {/* <h3>Coordenadas coletadas</h3> */}
                    <a
                      href={positionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver no Maps
                    </a>
                    
                    <table>

                    {freightData ? (

                      <CardFolowFreigth 
                          numberSerial={freightData.numberSerial}
                          product={freightData.freight.product}
                          weightCargo={freightData.freight.weightCargo}
                          valueNF={freightData.freight.valueNF}
                          valueFreightage={freightData.freight.valueFreightage}
                          user={freightData.user}
                          clientPayment={freightData.clientPayment}
                          vehicle={freightData.vehicle}
                          typeBodywork={freightData.vehicle?.typeBodywork?.dados ?? "Tipo não informado"}
                          typeVehicle={freightData.vehicle.typeVehicle.dados}
                          nameDriver={nameDriver}
                      />

                    ) : (
                      <ul>
                        <li>Dados do frete indisponíveis.</li>
                      </ul>
                    )}

                    </table>

                    <div style={{ marginTop: 10, marginBottom: 12, marginTop: 20 }}>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ marginRight: 8, fontWeight: 600 }}>
                          Validade:
                        </label>
                        <select
                          value={trackLinkValidityHours}
                          onChange={(e) =>
                            setTrackLinkValidityHours(Number(e.target.value))
                          }
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 8,
                            padding: "6px 8px",
                          }}
                        >
                          <option value={24}>24 horas</option>
                          <option value={72}>72 horas</option>
                          <option value={168}>7 dias</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={handleGeneratePublicTrackLink}
                        disabled={isGeneratingTrackLink}
                        style={{
                          border: "none",
                          background: "#012443",
                          color: "#fff",
                          padding: "8px 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                          marginRight: 8,
                        }}
                      >
                        {isGeneratingTrackLink
                          ? "Gerando..."
                          : "Gerar link público"}
                      </button>
                      <button
                        type="button"
                        onClick={handleDisablePublicTrackLink}
                        style={{
                          border: "1px solid #c62828",
                          background: "#fff",
                          color: "#c62828",
                          padding: "8px 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                        }}
                      >
                        Desativar link
                      </button>
                      <button
                        type="button"
                        onClick={handleShareViaWhatsApp}
                        disabled={!publicTrackUrl}
                        style={{
                          border: "none",
                          background: "#25D366",
                          color: "#fff",
                          padding: "8px 12px",
                          borderRadius: 8,
                          cursor: publicTrackUrl ? "pointer" : "not-allowed",
                          marginLeft: 8,
                        }}
                      >
                        Compartilhar WhatsApp
                      </button>
                      {publicTrackUrl && (
                        <div style={{ marginTop: 8, wordBreak: "break-all" }}>
                          {/* <a
                            href={publicTrackUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {publicTrackUrl}
                          </a> */}
                          <div
                            style={{
                              marginTop: 10,
                              display: "inline-block",
                              background: "#fff",
                              padding: 8,
                              borderRadius: 8,
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <QRCodeCanvas value={publicTrackUrl} size={132} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ width: "50%", marginBottom: 5 }}>
                    {/* <h3>Pontos de Parada</h3> */}
                    <a
                      href={stoppingPointsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver no Maps
                    </a>
                    <table className="table-pontos-de-parada">
                      
                      {freightData && freightData?.subcollectionData?.length > 0 ? (
                        
                        <StepPoints listPoints={freightData} />

                      ) : (
                        <li>Nenhum dado de ponto de parada disponível.</li>
                      )}
                    </table>
                  </div>

                </section>

                <section className="freight-alerts-panel">
                  <div className="freight-alerts-header">
                    <h3>Alertas operacionais</h3>
                    <select
                      value={alertFilter}
                      onChange={(e) => setAlertFilter(e.target.value)}
                    >
                      <option value="todos">Todos</option>
                      <option value="alto">Alta criticidade</option>
                      <option value="medio">Média criticidade</option>
                      <option value="baixo">Baixa criticidade</option>
                    </select>
                  </div>
                  {displayedAlerts.length === 0 ? (
                    <p className="freight-empty-state">Nenhum alerta ativo/persistido.</p>
                  ) : (
                    <ul className="freight-alert-list">
                      {displayedAlerts.map((alertItem) => (
                        <li key={alertItem.id || alertItem.fingerprint}>
                          <div>
                            <strong>{alertItem.title}</strong>
                            <p>{alertItem.description}</p>
                            <small>Fonte: {alertItem.source}</small>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const mapElement = document.querySelector(".mapa-comp-wrap");
                              if (mapElement) {
                                mapElement.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }
                            }}
                          >
                            Ver no mapa
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="freight-timeline-panel">
                  <div className="freight-alerts-header">
                    <h3>Timeline da viagem</h3>
                    <select
                      value={timelineFilter}
                      onChange={(e) => setTimelineFilter(e.target.value)}
                    >
                      <option value="todos">Todos</option>
                      <option value="alerta">Alertas</option>
                      <option value="parada">Paradas</option>
                    </select>
                  </div>
                  {timelineItems.length === 0 ? (
                    <p className="freight-empty-state">Sem eventos para o filtro selecionado.</p>
                  ) : (
                    <ul className="freight-timeline-list">
                      {timelineItems.map((item, index) => (
                        <li key={`${item.type}-${item.title}-${index}`}>
                          <span>{item.type === "alerta" ? "Alerta" : "Parada"}</span>
                          <div>
                            <strong>{item.title}</strong>
                            <small>
                              {formatDateTime(item.date)} - {item.source}
                            </small>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            ) : (
              <p>Nenhum ponto de parada encontrado.</p>
            )}
          </div>






        </div>
      }
    />
  );
}
